// Query builder + live results table.
//
// Mirrors the kinds of constraints the scripts/queries/*.sh CLI helpers
// can express: substring match across name/brand/store, category filter,
// THC range, displayed-price range, terpene-name + percent filter,
// dispensary restriction (from the preferred set), and sort.
//
// Updates instantly as the user changes anything.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useResizableWidth, ResultsTableHead, SORT_ASC, SORT_DESC, makeComparator } from "./table";
import { useVirtualRows } from "./virtual";
import type { ColumnDef, SortDir } from "./table";
import type { CategoryBlob, Filters, GeoCircle, ProductRow, StoreEntry } from "./types";

// Row shape after the filter pipeline rebases ppg/added on visibleStores.
interface VisibleRow extends ProductRow {
  visibleStores: StoreEntry[];
  added_date_visible: string | null;
  is_floor_added_visible: boolean;
}

function haversine_m(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1),
    dLon = toRad(lon2 - lon1);
  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2,
      ),
    )
  );
}

// dispensary_url is scraped third-party data; restrict it to http(s) so a
// javascript: URL can't ride into an href.  Returns null for anything else.
function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === "http:" || u.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

// ─── Price display unit ─────────────────────────────────────────────────────
// We show product prices as $/3.5g (one eighth) for flower. Internally
// every row carries `ppg`; for weight-priced categories that's $/g, for
// unit-priced ones (price_unit === "each": edible/tincture/topical) it's
// $-per-item -- see frontend/DATA_CONTRACT.md "Row shape additions".
// Filter and sort always use raw ppg internally; display multiplies by
// 3.5 for flower only. The price slider's unit/range follows the active
// category (blob), via priceModeFor().
const PRICE_UNIT_GRAMS = 3.5;
// Max observed: extract $240/g → cap $250/g. Flower $32/g → $112/3.5g → cap $120/3.5g.
const PPG_MAX_GRAM = 250; // vape/extract mode ($/g slider)
const PPG_MAX_3_5G = 120; // flower mode ($/3.5g slider); covers $34/g observed max
const PPG_MAX_EACH = 150; // edible/other mode ($-per-item slider)
const PPG_STEP = 1;

// Minimum width for the trailing flex (store) column, counted into the
// table's min-width floor so the column can't be squeezed away to nothing.
// Store labels measure up to ~186px of text plus 36px of cell padding; 200
// keeps the common ones ("Ethos Northeast Philadelphia", "2 stores") intact
// and ellipsizes only the longest, rather than collapsing the column.
const STORE_MIN_W = 200;

// Single source of truth for the per-category price-unit heading. Terse
// suffixes like "/3.5g" left shoppers guessing, so this spells the unit out.
// Every standalone price-unit heading pulls from here: the product-detail
// store-list header, the mobile sort chip, the desktop price column, and the
// filter slider.
export function priceUnitLabel(category: string): string {
  // "per item" read as one gummy on a 10-pack; ppg for these categories is
  // the price of the whole package.  A single syringe is a package of one,
  // so one wording serves a blob holding both.
  if (category === "edible" || category === "other") return "$ per package";
  if (category === "flower") return "$ per 1/8th";
  return "$ per gram";
}

// Price slider unit/range for the active category. Shared with
// hasActiveFilters() (app.tsx doesn't otherwise know the active blob).
export function priceModeFor(category: string): { mult: number; max: number } {
  if (category === "edible" || category === "other") return { mult: 1, max: PPG_MAX_EACH };
  if (category === "flower") return { mult: PRICE_UNIT_GRAMS, max: PPG_MAX_3_5G };
  return { mult: 1, max: PPG_MAX_GRAM };
}

// THC / total-terpene slider ranges, per category. These are not just display
// ranges: the sliders are minimum-only, so the upper bound is a constant that
// also acts as a ceiling in the filter pipeline. Flower's numbers are wrong
// for a concentrate by a wide margin -- measured over weedbud.db on
// 2026-07-31, 99.3% of vape products and 97.0% of extracts read above 40%
// THC, and 53% / 58% carry more than 5% total terpenes. Extract tops out at
// 99.8% THC and 24.6% terps; flower reaches 85.6% / 10.7% but only 1.5% /
// 0.03% of it clears the flower caps, so flower keeps its tighter ranges.
const THC_MAX_FLOWER = 40;
const THC_MAX_POTENT = 100;
const TERP_MAX_FLOWER = 5;
const TERP_MAX_POTENT = 25;

// Package THC in milligrams -- the potency axis on the unit-priced
// categories, where the percent is a concentration by mass rather than a
// strength. Measured over weedbud.db on 2026-08-11: pkg_mg runs 0.08mg to
// 3000mg with a median of 200mg (edible 250mg), and 1000mg is the 90th
// percentile in both edible and other, so the cap buys resolution over the
// common range while still allowing a "1000mg+" floor. Re-verify before
// relying on these numbers.
export const PKG_MG_MAX = 1000;

// Categories priced per package rather than per gram (DATA_CONTRACT.md).
// Same set that hides the percent thc/cbd/terps columns.
export function isUnitPriced(category: string): boolean {
  return category === "edible" || category === "other";
}

export function thcModeFor(category: string): { max: number } {
  return { max: category === "flower" ? THC_MAX_FLOWER : THC_MAX_POTENT };
}

export function terpModeFor(category: string): { max: number } {
  return { max: category === "flower" ? TERP_MAX_FLOWER : TERP_MAX_POTENT };
}

// The one row-filtering predicate, shared by QueryView's pipeline and the
// category-tab counts (app.tsx) so a tab's number is exactly what the view
// would show on switching to it — the two can never disagree.
export function makeRowFilter(
  filters: Filters,
  category: string,
  preferred: Set<string>,
): (r: ProductRow) => boolean {
  const priceMode = priceModeFor(category);
  const thcMode = thcModeFor(category);
  const terpMode = terpModeFor(category);
  const q = filters.q.trim().toLowerCase();
  const cats = filters.categories;
  const types = filters.types;
  const brands = filters.brands;
  const oils = filters.oils;
  const formats = filters.formats;
  const weights = filters.weights;
  const solventless = filters.solventless;
  const concentrateForms = filters.concentrateForms;
  const edibleForms = filters.edibleForms;
  const [doseLo, doseHi] = filters.dose;
  const flowerExclude = filters.flowerExclude;
  const otherForms = filters.otherForms;
  // A thumb at its extreme is unbounded on that side: the slider max is
  // the control's scale, not a filter, so a stored bound at (or beyond)
  // the active category's max means "no ceiling". This is what keeps
  // rows with out-of-scale readings (e.g. a mg-valued THC stored as
  // percent) visible at defaults instead of silently clipped -- and
  // keeps the tab counts honest, since defaults then filter nothing.
  const unb = (bound: number, modeMax: number) => (bound >= modeMax ? Infinity : bound);
  // The percent potency axes apply only where a percent is a strength.
  // On the unit-priced tabs the rail offers no thc/terp control at all, so
  // these must be inert rather than merely hidden -- a bound left in a
  // saved snapshot would otherwise filter with nothing left to clear it
  // (a terp floor drops every edible row, none of which carry a total).
  const percentPotency = !isUnitPriced(category);
  const thcLo = filters.thc[0];
  const thcHi = unb(filters.thc[1], thcMode.max);
  const pkgThcLo = filters.pkgThc[0];
  const pkgThcHi = unb(filters.pkgThc[1], PKG_MG_MAX);
  const ppgLo = filters.ppg[0];
  const ppgHi = unb(filters.ppg[1], priceMode.max);
  const terpName = filters.terpName;
  const terpLo = filters.terp[0];
  const terpHi = unb(filters.terp[1], terpMode.max);
  const [addedLo, addedHi] = filters.addedDaysAgo;
  const restrict = preferred.size > 0;

  return (r) => {
    if (cats.size && !cats.has(r.category as string)) return false;
    if (types.size && !types.has(r.strain_type as string)) return false;
    if (brands.size && !brands.has(r.brand as string)) return false;
    // Form facets (extract/vape). oil/format are OR-selects; solventless
    // is a boolean narrowing; weight matches if the product offers any
    // selected size.
    if (oils.size && !(r.oil && oils.has(r.oil))) return false;
    if (formats.size && !(r.vape_format && formats.has(r.vape_format))) return false;
    if (solventless && !r.solventless) return false;
    if (concentrateForms.size && !(r.concentrate_form && concentrateForms.has(r.concentrate_form)))
      return false;
    if (weights.size && !r.weights?.some((w) => weights.has(w))) return false;
    // Edible facets: form is an OR-select; dose is a range snapped to the
    // popular denominations, not a multi-select -- too many denominations
    // made for too many buttons. A product with no mg_each can't be
    // compared on that axis, so it drops once either bound is engaged.
    if (edibleForms.size && !(r.edible_form && edibleForms.has(r.edible_form))) return false;
    if (doseLo > 0 || isFinite(doseHi)) {
      if (r.mg_each == null || r.mg_each < doseLo || r.mg_each > doseHi) return false;
    }
    // Other-category facet (tincture/topical): form is an OR-select.
    if (otherForms.size && !(r.other_form && otherForms.has(r.other_form))) return false;
    // Flower facets are exclusion filters: everything shows by default,
    // and each active exclusion hides its class (infused adulteration, or
    // the smalls/ground grade from r.flower_form). Non-flower rows lack
    // these fields, so the guards are no-ops off the flower tab.
    if (flowerExclude.has("infused") && r.infused) return false;
    if (flowerExclude.has(r.flower_form as string)) return false;
    if (restrict && !r.stores.some((s) => preferred.has(s.dispensary_id + "|" + s.platform)))
      return false;
    // A row with no reading is dropped once either bound is engaged --
    // an engaged range means "show me products I can compare on this
    // axis", which an unknown value can't satisfy.
    if (percentPotency) {
      if (r.thc == null ? thcLo > 0 || isFinite(thcHi) : r.thc < thcLo || r.thc > thcHi)
        return false;
    } else if (
      r.pkg_mg == null
        ? pkgThcLo > 0 || isFinite(pkgThcHi)
        : r.pkg_mg < pkgThcLo || r.pkg_mg > pkgThcHi
    ) {
      return false;
    }
    if (r.ppg * priceMode.mult < ppgLo || r.ppg * priceMode.mult > ppgHi) return false;
    if (percentPotency) {
      if (terpName !== "any") {
        const entry = r.terps_top?.find(([n]) => n === terpName);
        if (!entry) return false;
        const pct = entry[1];
        if (pct < terpLo || pct > terpHi) return false;
      } else if (
        r.terps_total == null
          ? terpLo > 0 || isFinite(terpHi)
          : r.terps_total < terpLo || r.terps_total > terpHi
      ) {
        return false;
      }
    }
    if ((addedLo > 0 || isFinite(addedHi)) && r.added_date) {
      const days = Math.floor((Date.now() - new Date(r.added_date).getTime()) / 86400000);
      if (days < addedLo || days > addedHi) return false;
    }
    if (
      q &&
      !(
        (r.product_name || "").toLowerCase().includes(q) ||
        (r.brand || "").toLowerCase().includes(q) ||
        r.stores.some((s) => (s.dispensary_name || "").toLowerCase().includes(q))
      )
    )
      return false;
    return true;
  };
}

const AGE_STEPS = [1, 2, 3, 4, 5, 6, 7, 14, 21, 30, 60, 90, 180, 365, Infinity];
// The range control needs a zero floor AGE_STEPS lacks: its min thumb at
// index 0 must mean "no lower bound", and AGE_STEPS starts at 1 day, which
// would quietly exclude anything added today.
const AGE_RANGE_STEPS = [0, ...AGE_STEPS];
const fmtAge = (v: number) => {
  if (!isFinite(v)) return "any";
  if (v < 14) return v + "d";
  if (v < 30) return v / 7 + "w";
  if (v < 365) return Math.round(v / 30) + "mo";
  return "1yr";
};
// price_unit "each" rows (edible/tincture/topical) are never multiplied
// or given a "/g" suffix -- ppg is already $-per-item. Flower is the only
// $/3.5g category; everything else weight-priced stays $/g.
const priceRowDisp = (r: VisibleRow, ppg = r.ppg) =>
  r.price_unit === "each" ? ppg : r.category === "flower" ? ppg * PRICE_UNIT_GRAMS : ppg;

// ───────────────────────── filter controls ─────────────────────────────────

// Collapse-all chip: shared by mobile sort rail and desktop product header.
// stopPropagation so a click inside the th doesn't also fire the column sort.
// One pill, the same on both layouts, over the right end of whichever
// sort strip is on screen: the mobile rail or the desktop header row.
function CollapseAllBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      className="collapse-all-btn"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      title="Collapse all open product details"
      aria-label="Collapse all open product details"
    >
      {label}
    </button>
  );
}

interface ProductDetailProps {
  r: VisibleRow;
  onClose: () => void;
  geoCircle: GeoCircle | null;
  storeSort: StoreSort;
  onStoreSort: (key: string) => void;
}

// ─── Product detail (desktop + mobile) ──────────────────────────────────────
// One shared content component used by both layouts. Each wraps it in the
// element its container needs -- a <td colSpan> for the desktop <tr>, a <div>
// for the mobile <li> -- but both replace the row's collapsed content in
// place (see DesktopRow / MobileRow). Anything that differs between the two
// (wrapper element, colSpan) is a wrapper concern, not a content concern.
// The header doubles as the close affordance: clicking it (the same top line
// that opened the row) collapses it -- there's no separate close button.
const storeKey = (s: StoreEntry) => s.dispensary_id + "|" + s.platform;

function ProductDetailBody({ r, onClose, geoCircle, storeSort, onStoreSort }: ProductDetailProps) {
  // Sorted copy for display only.  visibleStores stays cheapest-first:
  // the row's own price is read off its head.
  const stores = r.visibleStores
    .slice()
    .sort(makeComparator<StoreEntry>(storeSort.key, storeSort.dir));
  // Min, not stores[0]: the list sorts on other columns.
  const bestPpg = Math.min(...stores.map((s) => s.ppg));
  // The terpene strip shows one store's profile: the selected row's, or
  // until one is picked, the cheapest store's -- the same store the
  // collapsed row's own figures come from.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected =
    (selectedKey && stores.find((s) => storeKey(s) === selectedKey)) || r.visibleStores[0] || null;
  const formLabel = formatForm(r.form);
  // Per-store readings.  The percent columns are concentrations by mass,
  // which mean nothing on a unit-priced product (the same rule hides them
  // from the table on those tabs); CBD only earns a column when some store
  // reports one.
  const percentCols = !isUnitPriced(r.category ?? "");
  const showCbd = percentCols && stores.some((s) => s.cbd != null && s.cbd > 0);
  const pct = (v: number | null | undefined, dp: number) => (v == null ? "—" : `${v.toFixed(dp)}%`);
  // Name, price, [thc, [cbd,] terps,] added.  Every track is as wide as its
  // widest value; only the name may shrink (and then ellipsize).
  const readingCols = 1 + (percentCols ? 2 : 0) + (showCbd ? 1 : 0) + 1;
  const storeGrid = {
    gridTemplateColumns: `minmax(0, max-content) repeat(${readingCols}, max-content)`,
  };
  // Each column's name is its sort control, as in the table's own header:
  // glyph leading the label, the active column in accent.  One row does
  // both jobs, so the controls and the columns cannot drift apart.
  const sortHead = (sortKey: string, label: string) => {
    const isActive = storeSort.key === sortKey;
    const dir = isActive ? storeSort.dir : (SORT_INITIAL_DIR[sortKey] ?? "asc");
    return (
      <button
        type="button"
        className={"store-sort" + (isActive ? " is-active" : "")}
        data-store-sort={sortKey}
        onClick={() => onStoreSort(sortKey)}
      >
        <span className={"sort-glyph" + (isActive ? " is-active" : "")}>
          {dir === "desc" ? SORT_DESC : SORT_ASC}
        </span>
        {label}
      </button>
    );
  };

  return (
    <>
      <div
        className="product-detail-header"
        onClick={onClose}
        title="Collapse"
        aria-label="Collapse"
      >
        <StrainTypeIcon t={r.strain_type} />
        <span className="product-detail-name-text">{r.product_name}</span>
      </div>
      <div className="product-detail-meta">
        {r.category && <span className={"cat-pill cat-" + r.category}>{r.category}</span>}
        {formLabel && <span className="product-detail-form">{formLabel}</span>}
        {r.brand && <span className="product-detail-brand">{r.brand}</span>}
        {r.price_unit === "each" && (
          // The dose is a property of the product, not of any store, so it
          // sits with the form and brand.  mg_each needs both pkg_mg and
          // pkg_count; platforms send no count for a patch or a syringe, so
          // the per-piece figure is null while the package dose is known --
          // show that instead, worded so it can't be read as a per-piece
          // strength.  A count of 1 gets no "× 1".
          <span className="product-detail-dose mono">
            {r.mg_each != null
              ? `${r.mg_each}mg${r.pkg_count != null && r.pkg_count > 1 ? ` × ${r.pkg_count}` : ""}`
              : r.pkg_mg != null
                ? `${r.pkg_mg}mg per package`
                : "—"}
          </span>
        )}
      </div>
      <div className="product-detail-body">
        {percentCols && selected && (
          // The selected store's profile, named so a reader knows whose
          // batch they are looking at.  A store that reports none says so
          // rather than vanishing, or the strip would jump as rows are
          // picked.
          <div className="product-detail-section product-detail-terps">
            <div className="product-detail-section-title">
              <span>Terpenes</span>
              <span className="product-detail-terps-store">{selected.dispensary_name}</span>
            </div>
            <div className="terp-strip">
              {selected.terps_top && selected.terps_top.length > 0 ? (
                selected.terps_top.map(([name, pct]) => (
                  <span key={name} className="terp-strip-item">
                    <span className="terp-strip-name">{name}</span>
                    <span className="terp-strip-pct">{pct.toFixed(2)}%</span>
                  </span>
                ))
              ) : (
                <span className="terp-strip-none">no terpene data at this store</span>
              )}
            </div>
          </div>
        )}
        <div className="product-detail-section product-detail-stores">
          <div className="product-detail-section-title">
            <span>{stores.length === 1 ? "1 store" : `${stores.length} stores`}</span>
          </div>
          <div className="product-detail-store-list" style={storeGrid}>
            <div className="product-detail-store-head">
              <span className="product-detail-store-col-name">
                {sortHead("dispensary_name", "store")}
              </span>
              <span className="product-detail-store-col">
                {sortHead("ppg", priceUnitLabel(r.category ?? ""))}
              </span>
              {percentCols && (
                <span className="product-detail-store-col">{sortHead("thc", "thc")}</span>
              )}
              {showCbd && <span className="product-detail-store-col">cbd</span>}
              {percentCols && (
                <span className="product-detail-store-col">{sortHead("terps_total", "terps")}</span>
              )}
              <span className="product-detail-store-col">{sortHead("added_date", "added")}</span>
            </div>
            {stores.map((s) => (
              <div
                key={storeKey(s)}
                className={
                  "product-detail-store-row" +
                  (s.ppg === bestPpg ? " is-best" : "") +
                  (selected && storeKey(s) === storeKey(selected) ? " is-selected" : "")
                }
                onClick={() => setSelectedKey(storeKey(s))}
              >
                <StoreLink store={s} geoCircle={geoCircle} />
                <span className="product-detail-store-col product-detail-store-price-group">
                  <span
                    className="product-detail-store-delta"
                    aria-hidden={s.ppg === bestPpg ? "true" : undefined}
                    style={s.ppg === bestPpg ? { visibility: "hidden" } : undefined}
                  >
                    +${(priceRowDisp(r, s.ppg) - priceRowDisp(r, bestPpg)).toFixed(0)}
                  </span>
                  <span
                    className={
                      "product-detail-store-price mono" +
                      (s.regular_ppg != null && s.regular_ppg > s.ppg ? " is-sale" : "")
                    }
                    title={
                      s.regular_ppg != null && s.regular_ppg > s.ppg
                        ? `was $${priceRowDisp(r, s.regular_ppg).toFixed(0)}`
                        : undefined
                    }
                  >
                    ${priceRowDisp(r, s.ppg).toFixed(0)}
                  </span>
                </span>
                {percentCols && (
                  <span className="product-detail-store-col mono product-detail-store-thc">
                    {pct(s.thc, 1)}
                  </span>
                )}
                {showCbd && (
                  <span className="product-detail-store-col mono product-detail-store-cbd">
                    {pct(s.cbd, 1)}
                  </span>
                )}
                {percentCols && (
                  <span className="product-detail-store-col mono product-detail-store-terps">
                    {pct(s.terps_total, 2)}
                  </span>
                )}
                <span className="product-detail-store-col mono product-detail-store-added">
                  {formatAddedDate(s.added_date) ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// Both layouts open a row by replacing its collapsed content in place (not by
// adding a sibling below), so closing shrinks the row element itself. That
// risks a scroll jump; this keeps the row visually put across the toggle.
// `anchorRef` is the element that stays mounted through the toggle (the <li>
// or <tr>), `contentRef` is the detail wrapper to reveal. The anchor's
// position is re-measured on every scroll, not just at mount, so closing
// never yanks back scrolling the user did while the panel was open.
function useDetailScrollKeep(
  anchorRef: React.RefObject<HTMLElement | null>,
  contentRef: React.RefObject<HTMLElement | null>,
  containerSel: string,
) {
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (content)
      content.scrollIntoView({ block: "nearest", behavior: "instant" as ScrollBehavior });
    const anchor = anchorRef.current;
    const container = anchor?.closest(containerSel) as HTMLElement | null;
    if (!anchor || !container) return;
    const topOf = () => anchor.getBoundingClientRect().top - container.getBoundingClientRect().top;
    let top = topOf();
    const onScroll = () => {
      top = topOf();
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      const drift = topOf() - top;
      if (Math.abs(drift) > 1) container.scrollTop -= drift;
    };
  }, [anchorRef, contentRef, containerSel]);
}

// The detail panel for the mobile list: a <div> inside the row's <li>.
function MobileProductDetail({
  liRef,
  ...props
}: ProductDetailProps & { liRef: React.RefObject<HTMLLIElement | null> }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useDetailScrollKeep(liRef, ref, ".m-list");
  return (
    <div ref={ref} className="product-detail-cell product-detail-mobile">
      <ProductDetailBody {...props} />
    </div>
  );
}

// ─── MobileRow ───────────────────────────────────────────────────────────
// Collapsed: product name + store (m-row-main). Open: the collapsed content
// is replaced in place by the full detail (which now carries the full
// product name + form, so nothing shown while collapsed is lost). Collapsed,
// a tap anywhere on the row opens it (the store link stops its own tap so it
// opens the popup instead); open, the detail's header (the same "top line
// with the product name", via onClose) closes it -- there's no separate
// close button. The <li> itself never unmounts across the toggle -- only its
// children swap -- so `liRef` stays a stable anchor for the scroll fixup.
// How long the box stays up after the list stops moving. Long enough to
// notice it and move a finger onto it; short enough that it is not
// permanent chrome. The accent ring and the 20px of grab slop make it
// quicker to acquire than the original translucent box was, which is what
// affords the shorter window.
const LINGER_MS = 1800;

// Pointer travel below which a press counts as a tap rather than a drag.
const TAP_SLOP_PX = 5;

// Fast-scroll rail for the mobile list: drag it to travel the whole list in
// one gesture instead of ~40 flicks, with a bubble naming where you are.
//
// Mobile only. Desktop's scrollbars are 12px and mouse-grabbable (see the
// html[data-mobile="0"] block in styles.css), which is the same affordance
// for pointer input -- a second one would be redundant chrome.
function ScrubBar({
  containerRef,
  fraction,
  value,
  productName,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  fraction: number;
  // The active sort axis's value where the thumb is -- what you aim with.
  // productName sits to its LEFT, clear of the finger on the rail.
  value: string | null;
  productName: string | null;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  // No rail and no thumb: the labelled box IS the handle. It appears when
  // the reader scrolls and lingers afterwards, long enough to lift a finger
  // off the list and put it back down on the box. Holding it keeps it up
  // indefinitely -- fading mid-drag would be worse than never appearing.
  //
  // Summoned by the gesture -- a finger on the list, a wheel -- not by the
  // scroll fraction moving.  The fraction is scrollTop over the list's
  // scrollable height, and that height changes when a row expands or the
  // viewport resizes, which put the box up with nobody scrolling.  The
  // fraction still keeps the box alive: a flick's momentum carries on after
  // the finger lifts, and each change restarts the linger.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const summon = () => setVisible(true);
    el.addEventListener("touchmove", summon, { passive: true });
    el.addEventListener("wheel", summon, { passive: true });
    return () => {
      el.removeEventListener("touchmove", summon);
      el.removeEventListener("wheel", summon);
    };
  }, [containerRef]);
  useEffect(() => {
    if (!visible || dragging) return;
    const t = setTimeout(() => setVisible(false), LINGER_MS);
    return () => clearTimeout(t);
  }, [visible, dragging, fraction]);

  // Map a pointer y to a scroll offset. Reads the container's live
  // scrollHeight rather than a cached total so an expanded row (which
  // changes the list's height) can't desync the thumb from the content.
  const scrollTo = (clientY: number) => {
    const track = trackRef.current;
    const el = containerRef.current;
    if (!track || !el) return;
    const r = track.getBoundingClientRect();
    const f = Math.min(1, Math.max(0, (clientY - r.top) / Math.max(1, r.height)));
    el.scrollTop = f * Math.max(0, el.scrollHeight - el.clientHeight);
  };

  // A press is not resolved as tap-or-drag until the pointer moves, so the
  // list must not be scrolled on pointerdown. Doing so made a tap either
  // nudge the list to wherever the finger landed or -- when it landed at the
  // box's own position -- appear to do nothing at all.
  const pressY = useRef(0);
  const moved = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragging(true);
    pressY.current = e.clientY;
    moved.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    e.preventDefault();
    if (!moved.current) {
      if (Math.abs(e.clientY - pressY.current) < TAP_SLOP_PX) return;
      moved.current = true;
    }
    scrollTo(e.clientY);
  };
  const endDrag = () => setDragging(false);

  // A tap dismisses it -- on the click, not on pointerup. Unmounting at
  // pointerup leaves the follow-up click with no target, and the browser
  // retargets it to whatever is now under the finger: the row, whose
  // .m-row-main carries onClick={onToggle}. So the tap would dismiss the box
  // AND expand a product. (preventDefault on pointerdown does not help --
  // it suppresses the compatibility mouse events, not the click.) Staying
  // mounted through the click keeps the box as the click's target.
  //
  // Not sticky: the next scroll re-summons it, since `fraction` changing is
  // what makes it visible in the first place.
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!moved.current) setVisible(false);
  };

  // Nothing in the DOM while hidden: an invisible overlay would still
  // swallow taps and scrolls along the list's right edge.
  if (!visible) return null;

  return (
    // The container only provides the coordinate space the drag maps
    // against; it must not intercept anything itself.
    <div className={"scrub" + (dragging ? " is-drag" : "")} ref={trackRef}>
      <div
        className="scrub-bubble"
        style={{ top: `${fraction * 100}%` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onClick}
      >
        {/* Name first: it trails away to the left, clear of the finger on
          the box. The value sits at the right, nearest the edge the eye is
          already tracking. */}
        {productName && <span className="scrub-sub">{productName}</span>}
        {/* Inline SVG, not a font character: ↕ has patchy coverage across
          Android system fonts and falls back to an emoji-styled glyph on
          several of them. */}
        <span className="scrub-grip" aria-hidden="true">
          <svg
            width="13"
            height="20"
            viewBox="0 0 13 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6.5 2.5v15" />
            <path d="M2.8 6.2 6.5 2.5l3.7 3.7" />
            <path d="M2.8 13.8 6.5 17.5l3.7-3.7" />
          </svg>
        </span>
        {value && <span className="scrub-value mono">{value}</span>}
      </div>
    </div>
  );
}

function MobileRow({
  r,
  isOpen,
  showStore,
  sortVal,
  geoCircle,
  storeSort,
  onStoreSort,
  onToggle,
  onMeasure,
}: {
  r: VisibleRow;
  isOpen: boolean;
  showStore: boolean;
  sortVal: string | null;
  geoCircle: GeoCircle | null;
  storeSort: StoreSort;
  onStoreSort: (key: string) => void;
  onToggle: () => void;
  // Reports the rendered <li> to the virtualizer so it can learn the
  // uniform collapsed height and any expanded row's extra height.
  onMeasure?: (el: HTMLLIElement | null) => void;
}) {
  const liRef = useRef<HTMLLIElement | null>(null);
  const setRef = (el: HTMLLIElement | null) => {
    liRef.current = el;
    onMeasure?.(el);
  };
  return (
    <li ref={setRef} className={"m-row" + (isOpen ? " is-open" : "")}>
      {isOpen ? (
        <MobileProductDetail
          r={r}
          liRef={liRef}
          onClose={onToggle}
          geoCircle={geoCircle}
          storeSort={storeSort}
          onStoreSort={onStoreSort}
        />
      ) : (
        <div className="m-row-main" onClick={onToggle}>
          <div className="m-row-product">
            <StrainTypeIcon t={r.strain_type} />
            <span className="m-row-name">{r.product_name}</span>
          </div>
          {showStore && (
            <div className="m-row-store">
              {r.visibleStores.length === 1 ? (
                <StoreLink store={r.visibleStores[0]} geoCircle={geoCircle} />
              ) : (
                <span className="store-count">{r.visibleStores.length} stores</span>
              )}
            </div>
          )}
          {sortVal && <span className="m-row-sort-val">{sortVal}</span>}
        </div>
      )}
    </li>
  );
}

// The detail panel for the desktop table: a single <td colSpan> inside the
// row's <tr>.
function InlineProductDetail({
  colSpan,
  trRef,
  ...props
}: ProductDetailProps & { colSpan: number; trRef: React.RefObject<HTMLTableRowElement | null> }) {
  const ref = useRef<HTMLTableCellElement | null>(null);
  useDetailScrollKeep(trRef, ref, ".table-wrap");
  return (
    <td ref={ref} colSpan={colSpan} className="product-detail-cell">
      <ProductDetailBody {...props} />
    </td>
  );
}

// ─── DesktopRow ──────────────────────────────────────────────────────────
// Desktop counterpart of MobileRow, same model. Collapsed: normal per-column
// cells (td-product name + brand/thc/etc). Open: the row's own cells are
// replaced in place by a single colSpan detail cell -- not a sibling row
// below. Click-to-toggle lives only on the name (.td-product-name); the
// detail's header (via onClose) closes it -- clicking other columns, store
// links or terp values does nothing, and there's no separate close button.
function DesktopRow({
  r,
  isOpen,
  visibleCols,
  onToggle,
  geoCircle,
  storeSort,
  onStoreSort,
  onMeasure,
}: {
  r: VisibleRow;
  isOpen: boolean;
  visibleCols: QueryColDef[];
  onToggle: () => void;
  geoCircle: GeoCircle | null;
  storeSort: StoreSort;
  onStoreSort: (key: string) => void;
  // Reports the rendered <tr> to the virtualizer (see MobileRow.onMeasure).
  onMeasure?: (el: HTMLTableRowElement | null) => void;
}) {
  const trRef = useRef<HTMLTableRowElement | null>(null);
  const setRef = (el: HTMLTableRowElement | null) => {
    trRef.current = el;
    onMeasure?.(el);
  };
  return (
    <tr
      ref={setRef}
      // When open, the row *is* the detail (its cells are replaced by one
      // colSpan detail cell), so it carries both the is-selected state hook
      // and product-detail-row -- the latter names "this row is presenting
      // the detail" for styling and tests.
      className={"result-row" + (isOpen ? " is-selected product-detail-row" : "")}
      // Collapsed: clicking anywhere on the row opens it (store links guard
      // their own clicks). Open: the tr does nothing -- the detail's header
      // (via onClose) is what closes it, so a click on a store link or terp
      // value inside the panel doesn't collapse it out from under the user.
      onClick={() => {
        if (isOpen) return;
        onToggle();
      }}
    >
      {isOpen ? (
        <InlineProductDetail
          r={r}
          colSpan={visibleCols.length}
          trRef={trRef}
          onClose={onToggle}
          geoCircle={geoCircle}
          storeSort={storeSort}
          onStoreSort={onStoreSort}
        />
      ) : (
        visibleCols.map((col) => <React.Fragment key={col.key}>{col.renderCell(r)}</React.Fragment>)
      )}
    </tr>
  );
}

interface DualControlProps {
  label: string;
  suffix: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  color?: string;
}

// Two-thumb range slider. A thumb parked at its extreme is unbounded on
// that side ("any") -- the slider's max is the control's scale, not a
// filter, so the default position filters nothing. Stored values may
// exceed `max` (a wider category's bound); they clamp for display but
// still mean "unbounded" here.
function DualControl({
  label,
  suffix,
  min,
  max,
  step,
  value,
  onChange,
  color = "var(--accent)",
}: DualControlProps) {
  const lo = Math.max(min, Math.min(value[0], max));
  const hi = Math.max(lo, Math.min(value[1], max));
  const loPct = ((lo - min) / (max - min)) * 100;
  const hiPct = ((hi - min) / (max - min)) * 100;
  const fmt = (v: number) => (suffix === "$" ? `$${v}` : `${v}${suffix}`);
  const loOff = lo <= min;
  const hiOff = hi >= max;
  const text =
    loOff && hiOff
      ? "any"
      : loOff
        ? `≤ ${fmt(hi)}`
        : hiOff
          ? `≥ ${fmt(lo)}`
          : `${fmt(lo)}–${fmt(hi)}`;
  return (
    <div className="ctrl">
      <div className="ctrl-row">
        <span className="ctrl-label">{label}</span>
        <span className={"ctrl-value" + (loOff && hiOff ? " ctrl-value-dim" : "")}>{text}</span>
      </div>
      <div className="dual-range">
        <div className="single-range-track" />
        <div
          className="single-range-fill"
          style={{ left: `${loPct}%`, right: `${100 - hiPct}%`, background: color }}
        />
        <input
          type="range"
          data-thumb="min"
          min={min}
          max={max}
          step={step}
          value={lo}
          onChange={(e) => onChange([Math.min(+e.target.value, hi), hi])}
        />
        <input
          type="range"
          data-thumb="max"
          min={min}
          max={max}
          step={step}
          value={hi}
          onChange={(e) => onChange([lo, Math.max(+e.target.value, lo)])}
        />
      </div>
    </div>
  );
}

// Range slider for axes whose meaningful values are a small discrete set
// (age buckets, mg denominations) rather than a continuum: both thumbs move
// over the INDEX of a fixed step list. `steps[0]` is the unbounded floor and the
// last entry must be Infinity, the unbounded ceiling -- so with both
// thumbs parked at their extremes the control filters nothing, matching
// DualControl's contract on the continuous sliders.
function SnappedRangeControl({
  label,
  steps,
  value,
  onChange,
  format,
}: {
  label: string;
  steps: number[];
  value: [number, number];
  onChange: (v: [number, number]) => void;
  format: (n: number) => string;
}) {
  const last = steps.length - 1;
  const idxOf = (v: number) => {
    const i = steps.findIndex((s) => s >= v);
    return i < 0 ? last : i;
  };
  const loIdx = Math.min(idxOf(value[0]), last);
  const hiIdx = Math.max(loIdx, idxOf(value[1]));
  const loOff = loIdx === 0;
  const hiOff = hiIdx === last;
  const text =
    loOff && hiOff
      ? "any"
      : loOff
        ? `≤ ${format(steps[hiIdx])}`
        : hiOff
          ? `≥ ${format(steps[loIdx])}`
          : `${format(steps[loIdx])}–${format(steps[hiIdx])}`;
  return (
    <div className="ctrl">
      <div className="ctrl-row">
        <span className="ctrl-label">{label}</span>
        <span className={"ctrl-value" + (loOff && hiOff ? " ctrl-value-dim" : "")}>{text}</span>
      </div>
      <div className="dual-range">
        <div className="single-range-track" />
        <div
          className="single-range-fill"
          style={{
            left: `${(loIdx / last) * 100}%`,
            right: `${100 - (hiIdx / last) * 100}%`,
            background: "var(--accent)",
          }}
        />
        <input
          type="range"
          data-thumb="min"
          min={0}
          max={last}
          step={1}
          value={loIdx}
          onChange={(e) => onChange([steps[Math.min(+e.target.value, hiIdx)], steps[hiIdx]])}
        />
        <input
          type="range"
          data-thumb="max"
          min={0}
          max={last}
          step={1}
          value={hiIdx}
          onChange={(e) => onChange([steps[loIdx], steps[Math.max(+e.target.value, loIdx)]])}
        />
      </div>
    </div>
  );
}

function AgeControl({
  value,
  onChange,
}: {
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  return (
    <SnappedRangeControl
      label="Added (days ago)"
      steps={AGE_RANGE_STEPS}
      value={value}
      onChange={onChange}
      format={fmtAge}
    />
  );
}

function ChipMulti({
  options,
  value,
  onChange,
  label,
}: {
  options: string[];
  value: Set<string>;
  onChange: (v: Set<string>) => void;
  // Optional display transform; the chip's value stays the raw option.
  label?: (o: string) => string;
}) {
  const has = (o: string) => value.has(o);
  const toggle = (o: string) => {
    const n = new Set(value);
    if (n.has(o)) n.delete(o);
    else n.add(o);
    onChange(n);
  };
  return (
    <div className="chiprow">
      {options.map((o) => (
        <button key={o} className={"chip " + (has(o) ? "is-on" : "")} onClick={() => toggle(o)}>
          {label ? label(o) : o}
        </button>
      ))}
    </div>
  );
}

// Numeric-value chips (gram weights, mg doses, …) carry a Set<number> rather
// than ChipMulti's string set; `format` renders each option's label.
function NumericChips({
  options,
  value,
  onChange,
  format,
}: {
  options: number[];
  value: Set<number>;
  onChange: (v: Set<number>) => void;
  format: (n: number) => string;
}) {
  const toggle = (w: number) => {
    const n = new Set(value);
    if (n.has(w)) n.delete(w);
    else n.add(w);
    onChange(n);
  };
  return (
    <div className="chiprow">
      {options.map((w) => (
        <button
          key={w}
          className={"chip " + (value.has(w) ? "is-on" : "")}
          onClick={() => toggle(w)}
        >
          {format(w)}
        </button>
      ))}
    </div>
  );
}

// "0.5g" / "1g" / "3.5g" / "4g" (JS number-to-string already drops ".0").
function formatWeight(w: number): string {
  return `${w}g`;
}

// "1mg" / "2.5mg" / "10mg" / "1000mg" — per-piece edible dose.
function formatDose(d: number): string {
  return `${d}mg`;
}

// Human labels for the oil (live vs distillate) and vape-format facets.
// Lowercase, matching every other filter chip in the rail (strain type,
// category, brand chips render their raw -- already lowercase -- value).
const OIL_LABELS: Record<string, string> = { live: "live", distillate: "distillate" };
const FORMAT_LABELS: Record<string, string> = {
  cartridge: "cartridge",
  disposable: "disposable",
  aio: "all-in-one",
  pod: "pod",
};
// Human labels for the concentrate-form facet (backend/forms.py
// concentrate_form()). The badder bucket also covers budder/batter; the rest
// render their raw (already lowercase) value.
const CONCENTRATE_FORM_LABELS: Record<string, string> = { badder: "badder / budder" };
// Human labels for the edible-form facet (backend/forms.py edible_form()).
// The "capsule" bucket (capsules/tablets/pills/softgels) shows as "pill".
const EDIBLE_FORM_LABELS: Record<string, string> = {
  troche: "troche / lozenge",
  capsule: "pill",
  syrup: "syrup",
  gummy: "gummy",
  syringe: "syringe",
};
// Display order for the edible Form chips (data_from_db.py:EDIBLE_FORM_CHIPS
// picks *which* appear; this fixes the order). "pill" sits between "syringe"
// and "syrup" so the two /^syr/ labels never render adjacent. Present chips
// are filtered from this order, so absent forms just drop out.
const EDIBLE_FORM_ORDER = ["troche", "syringe", "capsule", "syrup"];
// Human labels for the other-form facet (backend/forms.py other_form()),
// the tincture/topical panel's Form chips. Empty -- tincture/lotion/patch
// all display as their raw bucket key.
const OTHER_FORM_LABELS: Record<string, string> = {};
const facetLabel = (map: Record<string, string>, o: string) => map[o] ?? o;

// Flower-panel exclusion chips, in display order. `key` matches the exclude
// set / row axis: "smalls" | "ground" are r.flower_form values, "infused" is
// r.infused. A chip renders only when the blob carries that class (see the
// flower Type section). singular = the thing hidden, for the toggle tooltip.
const FLOWER_EXCLUDE_CHIPS: { key: string; label: string; hides: string }[] = [
  { key: "smalls", label: "small buds", hides: "small buds" },
  { key: "ground", label: "pre-ground", hides: "pre-ground / shake" },
  { key: "infused", label: "infused", hides: "infused flower" },
];
// Every class starts hidden: the default flower view is plain whole flower.
const DEFAULT_FLOWER_EXCLUDE: readonly string[] = FLOWER_EXCLUDE_CHIPS.map((c) => c.key);

function StrainTypeIcon({ t }: { t: string | null }) {
  // simple typographic dot — i / s / h; null renders an invisible spacer to keep name column aligned
  if (!t) return <span className="stype" aria-hidden="true" />;
  return (
    <span className={`stype stype-${t}`}>{t === "indica" ? "i" : t === "sativa" ? "s" : "h"}</span>
  );
}

function PopupFooter({
  distMi,
  mapsUrl,
  shopUrl,
}: {
  distMi: number | null;
  mapsUrl: string | null;
  shopUrl: string | null;
}) {
  return (
    <>
      {distMi !== null && <span className="wb-popup-dist">{distMi} mi</span>}
      {mapsUrl && (
        <a className="wb-popup-dir" href={mapsUrl} target="_blank" rel="noreferrer">
          Directions ↗
        </a>
      )}
      {shopUrl && (
        <a className="wb-popup-shop" href={shopUrl} target="_blank" rel="noreferrer">
          Shop ↗
        </a>
      )}
    </>
  );
}

// Clickable store name — opens a fixed popup with distance + directions.
function StoreLink({ store, geoCircle }: { store: StoreEntry; geoCircle: GeoCircle | null }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pos) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPos(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [pos]);

  const disp = window.WEEDBUD_DATA!.dispensaries.find(
    (x) => x.dispensary_id === store.dispensary_id && x.platform === store.platform,
  );
  const mapsUrl = disp
    ? `https://www.google.com/maps/dir/?api=1&destination=${disp.latitude},${disp.longitude}`
    : null;
  const distMi =
    geoCircle && disp
      ? Math.round(
          haversine_m(geoCircle.lat, geoCircle.lng, disp.latitude, disp.longitude) / 1609.34,
        )
      : null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (pos) {
      setPos(null);
      return;
    }
    const rect = btnRef.current!.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left });
  };

  return (
    <>
      <button ref={btnRef} className="store-link" onClick={handleClick}>
        {store.dispensary_name}
      </button>
      {pos && (
        <div ref={popRef} className="store-popup" style={{ top: pos.top, left: pos.left }}>
          <PopupFooter
            distMi={distMi}
            mapsUrl={mapsUrl}
            shopUrl={safeHttpUrl(disp?.dispensary_url)}
          />
        </div>
      )}
    </>
  );
}

// ─────────────────────── column config ─────────────────────────────────────

// First-click sort direction per column key.  Columns where descending
// is the natural "most useful" order (THC, terpenes) start high→low;
// everything else starts low→high / A→Z.
const SORT_INITIAL_DIR: Record<string, SortDir> = {
  thc: "desc",
  cbd: "desc",
  terps_total: "desc",
  mg_each: "desc",
  pkg_mg: "desc",
  added_date_visible: "desc",
  added_date: "desc", // the store list's counterpart of added_date_visible
};

const MOBILE_SORT_CHIPS: { label: string; sortKey: string }[] = [
  { label: "PRICE", sortKey: "ppg" },
  { label: "THC", sortKey: "thc" },
  { label: "TERPS", sortKey: "terps_total" },
  { label: "NEW", sortKey: "added_date_visible" },
  { label: "NAME", sortKey: "product_name" },
];

// "each"-priced categories swap thc/terps in the mobile rail too, for the
// same figure their table column shows: per-piece dose on edible, package
// THC on other.
const MOBILE_SORT_CHIPS_DOSE: { label: string; sortKey: string }[] = [
  { label: "PRICE", sortKey: "ppg" },
  { label: "DOSE", sortKey: "mg_each" },
  { label: "NEW", sortKey: "added_date_visible" },
  { label: "NAME", sortKey: "product_name" },
];

const MOBILE_SORT_CHIPS_PKG_THC: { label: string; sortKey: string }[] = [
  { label: "PRICE", sortKey: "ppg" },
  { label: "THC (MG)", sortKey: "pkg_mg" },
  { label: "NEW", sortKey: "added_date_visible" },
  { label: "NAME", sortKey: "product_name" },
];

// Sort for the store list inside an expanded row, keyed on StoreEntry
// fields.  Every column of the list except CBD is sortable, so the
// sortable set follows the columns: percent THC and terpenes go with
// their columns on unit-priced tabs.  One shared sort for every open
// row: set on one expansion, already in place when the next opens.
export interface StoreSort {
  key: string;
  dir: SortDir;
}
export const DEFAULT_STORE_SORT: StoreSort = { key: "ppg", dir: "asc" };

// The table's click rule, shared with the store list: a new column starts
// in its natural direction; the active column flips.
function nextSort(current: { key: string; dir: SortDir }, key: string): StoreSort {
  const initial = SORT_INITIAL_DIR[key] ?? "asc";
  const opposite: SortDir = initial === "asc" ? "desc" : "asc";
  return { key, dir: current.key === key && current.dir === initial ? opposite : initial };
}

// Extends ColumnDef with the query-view-specific fields. Column widths are
// fixed (ColumnDef.width, set directly below) -- no drag-to-resize.
interface QueryColDef extends ColumnDef {
  visible: boolean;
  renderCell: (r: VisibleRow) => React.ReactNode;
}

// ─────────────────────────── main view ─────────────────────────────────────

export interface QueryViewProps {
  blob: CategoryBlob;
  preferred: Set<string>;
  setPreferred: React.Dispatch<React.SetStateAction<Set<string>>>;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  geoCircle: GeoCircle | null;
  isMobile: boolean;
  filterDrawerOpen: boolean;
  setFilterDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  railCollapsed: boolean;
  setRailCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  onFilteredCountChange?: (n: number) => void;
}

export function QueryView({
  blob,
  preferred,
  setPreferred,
  filters,
  setFilters,
  geoCircle,
  isMobile,
  filterDrawerOpen,
  setFilterDrawerOpen,
  railCollapsed,
  setRailCollapsed,
  onFilteredCountChange,
}: QueryViewProps) {
  // dispensaries/welcome/about ship only in the boot blob (DATA_CONTRACT.md);
  // everything else (rows/brands/categories/strain_types/terpene_names)
  // comes from the active category's blob, which may differ from boot.
  const WD = window.WEEDBUD_DATA!;
  // "each"-priced categories (edible/tincture/topical) hide the percent
  // thc/cbd/terps columns -- the percent is a concentration by mass there,
  // not a strength (DATA_CONTRACT.md). What replaces it differs:
  //
  //   edible -> mg_each, the per-piece dose a shopper counts out
  //   other  -> pkg_mg, the package THC
  //
  // 'other' cannot use mg_each: it needs a pack count, and the platforms
  // holding most of that category send none for a single-item product
  // (dutchie 0 of 450, jane 2 of 104), so the column was blank for ~70% of
  // the tab and no potency showed at all. pkg_mg covers 1023 of 1514.
  const unitPriced = isUnitPriced(blob.blob);
  const isDose = blob.blob === "edible";
  const isPkgThc = blob.blob === "other";

  // ── Inline product detail + watches state ─────────────────────────────
  const [expandedDetail, setExpandedDetail] = useState<Set<string>>(() => new Set());
  const toggleDetail = (key: string) =>
    setExpandedDetail((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  // Sort for the store list in every open row.  Its own state, not part
  // of `filters`: a filters change collapses every open row (below), and
  // re-sorting the list you are looking at must not close it.  Persisted
  // like a column width -- a preference, not a query.
  const [storeSort, setStoreSort] = useState<StoreSort>(() => {
    try {
      const raw = localStorage.getItem("wb.storesort.v1");
      const v = raw ? (JSON.parse(raw) as Partial<StoreSort>) : null;
      if (v && typeof v.key === "string" && (v.dir === "asc" || v.dir === "desc"))
        return { key: v.key, dir: v.dir };
    } catch {
      /* unreadable: start from the default */
    }
    return DEFAULT_STORE_SORT;
  });
  useEffect(() => {
    try {
      localStorage.setItem("wb.storesort.v1", JSON.stringify(storeSort));
    } catch {
      /* quota */
    }
  }, [storeSort]);
  const onStoreSort = useCallback((key: string) => setStoreSort((s) => nextSort(s, key)), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedDetail(new Set());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const update = (patch: Partial<Filters>) => setFilters({ ...filters, ...patch });

  // The price slider's unit/range follows the active category (blob):
  // $/3.5g for flower, $-each for edible/other, $/g otherwise. mult is
  // the factor applied to r.ppg before comparing against filters.ppg.
  const priceMode = priceModeFor(blob.blob);
  const thcMode = thcModeFor(blob.blob);
  const terpMode = terpModeFor(blob.blob);
  // ─── Apply filters ───────────────────────────────────────────────────────
  const filtered = useMemo<VisibleRow[]>(() => {
    // Two-stage pipeline so price stats and sorting reflect only the
    // user's chosen stores: filter first, then project each row down to
    // the subset of its stores that are in `preferred`.  When no
    // restriction is active (`preferred.size === 0`) visibleStores is
    // the full list, so behaviour is unchanged.
    const restrict = preferred.size > 0;
    return blob.rows.filter(makeRowFilter(filters, blob.blob, preferred)).map<VisibleRow>((r) => {
      const visibleStores = restrict
        ? r.stores.filter((s) => preferred.has(s.dispensary_id + "|" + s.platform))
        : r.stores;
      // Override ppg with the cheapest visible-store price so the column
      // and sort match what the user will actually see.
      // Compute added_date_visible: earliest first-seen among visible stores.
      let addedVis: string | null = null;
      let isFloorVis = false;
      for (const s of visibleStores) {
        if (s.added_date && (!addedVis || s.added_date < addedVis)) {
          addedVis = s.added_date;
          isFloorVis = !!s.is_floor_added;
        }
      }
      return {
        ...r,
        visibleStores,
        ppg: visibleStores[0]?.ppg ?? r.ppg,
        added_date_visible: addedVis ?? r.added_date,
        is_floor_added_visible: addedVis ? isFloorVis : r.is_floor_added,
      };
    });
    // The per-category modes are derived inside makeRowFilter from
    // blob.blob, so blob covers them.
  }, [filters, preferred, blob]);

  // Notify parent (App) of the live post-filter row count so the topbar
  // "Filter N products" button label stays in sync.
  useEffect(() => {
    onFilteredCountChange?.(filtered.length);
  }, [filtered.length, onFilteredCountChange]);

  const sorted = useMemo(
    () => filtered.slice().sort(makeComparator<VisibleRow>(filters.sort.key, filters.sort.dir)),
    [filtered, filters.sort],
  );

  // Both views render one continuous virtualized scroll -- no pages. The
  // 200-row page was arbitrary in both: boundaries fell wherever row 201
  // happened to land in whatever sort was active, and reaching them cost
  // ~13 screens of scrolling on mobile / ~11 on desktop.
  const keyAt = useCallback((i: number) => sorted[i]?.key ?? "", [sorted]);
  const mListRef = useRef<HTMLUListElement | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const vwin = useVirtualRows(sorted.length, mListRef, expandedDetail, keyAt, isMobile);
  const dwin = useVirtualRows(sorted.length, tableWrapRef, expandedDetail, keyAt, !isMobile);

  // Restricted-to list: collapsed by default.
  const [prefExpanded, setPrefExpanded] = useState(false);

  // Reset page/detail and collapse the preferred list when filters or preferred
  // stores change.  "Adjust during render" avoids the extra commit cycle of an
  // effect (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  const [prevFilters, setPrevFilters] = useState(filters);
  const [prevPreferred, setPrevPreferred] = useState(preferred);
  if (prevFilters !== filters || prevPreferred !== preferred) {
    setPrevFilters(filters);
    setPrevPreferred(preferred);
    setExpandedDetail(new Set());
    if (prevPreferred !== preferred) setPrefExpanded(false);
  }

  // CBD column shows only when the user has explicitly filtered for the
  // "cbd" strain type -- it's noise on a normal THC-focused browse.
  const showCbdCol = filters.types.has("cbd");

  const setSort = (key: string) => {
    setFilters((f) => ({ ...f, sort: nextSort(f.sort, key) }));
  };

  const resetFilters = () => setFilters(defaultFilters());

  // ─── Resizable rail ──────────────────────────────────────────────────────
  const [railW, dragRail] = useResizableWidth(
    "wb.rail.v1",
    280,
    220,
    Math.max(520, window.innerWidth - 300),
  );

  // ─── Column definitions ─────────────────────────────────────────────────
  // Table-driven: each col declares its display spec + a fixed width. The
  // flex `store` col (no width -- fills remaining space) is appended
  // afterwards. Mobile list reuses just `product` and `store`.
  const cols: QueryColDef[] = [
    {
      key: "product",
      title: "product",
      sortKey: "product_name",
      // 80th-percentile product_name length across all rows is 34 chars
      // (sqlite: length(product_name) at the 0.80 offset) -- 360px gives
      // that comfortable room (icon + gap + padding overhead) without
      // hunting for an exact-fit pixel value.
      width: 360,
      visible: true,
      renderCell: (r) => (
        <td className="td-product">
          <div className="td-product-name">
            <StrainTypeIcon t={r.strain_type} />
            <span className="td-product-text">{r.product_name}</span>
          </div>
        </td>
      ),
    },
    {
      key: "brand",
      title: "brand",
      sortKey: "brand",
      width: 160,
      visible: true,
      renderCell: (r) => (
        <td className="td-brand">{r.brand == null ? <span className="dim">—</span> : r.brand}</td>
      ),
    },
    {
      key: "thc",
      title: "thc",
      sortKey: "thc",
      isNumeric: true,
      width: 96,
      visible: !unitPriced,
      renderCell: (r) => (
        <td className="td-num mono td-thc">
          {r.thc == null ? (
            <span className="dim">—</span>
          ) : (
            <span className="num-fixed">{r.thc.toFixed(1)}%</span>
          )}
        </td>
      ),
    },
    {
      key: "cbd",
      title: "cbd",
      sortKey: "cbd",
      isNumeric: true,
      width: 96,
      visible: showCbdCol && !unitPriced,
      renderCell: (r) => (
        <td className="td-num mono td-cbd">
          {r.cbd == null || r.cbd === 0 ? (
            <span className="dim">—</span>
          ) : (
            <span className="num-fixed">{r.cbd.toFixed(1)}%</span>
          )}
        </td>
      ),
    },
    {
      key: "terps",
      title: "terps",
      sortKey: "terps_total",
      isNumeric: true,
      width: 120,
      visible: !unitPriced,
      renderCell: (r) => (
        <td className="td-num mono td-terps">
          {r.terps_total == null ? (
            <span className="dim">—</span>
          ) : (
            <span className="num-fixed">{r.terps_total.toFixed(2)}%</span>
          )}
        </td>
      ),
    },
    {
      key: "dose",
      title: "dose",
      sortKey: "mg_each",
      isNumeric: true,
      width: 110,
      visible: isDose,
      renderCell: (r) => (
        <td className="td-num mono td-dose">
          {r.mg_each == null ? (
            <span className="dim">—</span>
          ) : (
            <span className="num-fixed">{r.mg_each}mg</span>
          )}
        </td>
      ),
    },
    {
      // 'other' only. Milligrams of THC in the package, not a percent and
      // not a per-piece dose -- hence the explicit unit in the heading.
      key: "pkg_thc",
      title: "thc (mg)",
      sortKey: "pkg_mg",
      isNumeric: true,
      // Same headroom rule as ppg below: the heading, not the value, sets
      // the floor here -- "1500mg" is far shorter than "THC (MG)" plus a
      // sort arrow.
      width: 132,
      visible: isPkgThc,
      renderCell: (r) => (
        <td className="td-num mono td-pkg-thc">
          {r.pkg_mg == null ? (
            <span className="dim">—</span>
          ) : (
            <span className="num-fixed">{r.pkg_mg}mg</span>
          )}
        </td>
      ),
    },
    {
      key: "ppg",
      title: priceUnitLabel(blob.blob),
      sortKey: "ppg",
      isNumeric: true,
      // Sized for the longest price-unit label, "$ per package", plus its
      // sort arrow. th is text-align:right + overflow:hidden, so a label
      // that outgrows the column clips its tail rather than wrapping.
      //
      // The margin is deliberately wide rather than snug. The heading sits
      // at 10.5px with 0.1em letter-spacing, where a few pixels is a whole
      // character, and the shipped webfont is not always the face that
      // renders -- a fallback monospace can be materially wider. This holds
      // the label near 70% of usable width, which absorbs that. Widening
      // further comes out of the product and store columns.
      width: 176,
      visible: true,
      renderCell: (r) => {
        const onSale = r.regular_ppg != null && r.regular_ppg > r.ppg;
        return (
          <td className={"td-num mono td-price" + (onSale ? " is-sale" : "")}>
            {onSale && r.regular_ppg != null && (
              <span className="td-price-was">${priceRowDisp(r, r.regular_ppg).toFixed(0)}</span>
            )}
            <span className="td-price-now">${priceRowDisp(r).toFixed(0)}</span>
          </td>
        );
      },
    },
    {
      key: "added",
      title: "added",
      sortKey: "added_date_visible",
      isNumeric: true,
      width: 116,
      visible: true,
      renderCell: (r) => (
        <td className={"td-num mono td-added" + (r.is_floor_added_visible ? " is-floor" : "")}>
          {r.added_date_visible ? (
            <span title={r.added_date_visible}>{formatAddedDate(r.added_date_visible)}</span>
          ) : (
            <span className="dim">—</span>
          )}
        </td>
      ),
    },
    // Trailing flex column: no fixed width, fills whatever space the fixed
    // columns don't use.
    {
      key: "store",
      title: "store",
      sortKey: null,
      flex: true,
      visible: true,
      renderCell: (r) => (
        <td className="td-store">
          {r.visibleStores.length === 1 ? (
            <StoreLink store={r.visibleStores[0]} geoCircle={geoCircle} />
          ) : (
            <span className="store-count">{r.visibleStores.length} stores</span>
          )}
        </td>
      ),
    },
  ];

  const visibleCols = cols.filter((c) => c.visible);
  // Table min-width floor (see .results-table in styles.css).
  // table-layout:fixed otherwise shrinks columns to fit the wrapper --
  // silently ignoring their set widths -- whenever their natural sum exceeds
  // the available space; this floor makes the table overflow (and
  // .table-wrap scroll horizontally) instead, so a column's width is honored.
  //
  // The flex column (store) must contribute STORE_MIN_W, not zero. Summing
  // only the fixed columns let the flex one absorb every pixel of shrink
  // down to 0px wide before the table exceeded the wrapper: the store
  // column silently vanished, and the scrollbar only appeared once the
  // fixed columns alone overflowed -- by which point the whole store column
  // was gone and ADDED was starting to clip.
  const tableMinWidth =
    visibleCols.filter((c) => !c.flex).reduce((sum, c) => sum + (c.width ?? 0), 0) +
    visibleCols.filter((c) => c.flex).length * STORE_MIN_W;

  // Mobile rows show only product + store. Aside from the wrapper element
  // (<li> vs <tr>), the data they pull is the same as the desktop columns.
  const mobileSortVal = (r: VisibleRow): string | null => {
    const k = filters.sort.key;
    if (k === "product_name") return null;
    if (k === "ppg") return `$${priceRowDisp(r).toFixed(0)}`;
    if (k === "thc") return r.thc == null ? null : `${r.thc.toFixed(1)}%`;
    if (k === "terps_total") return r.terps_total != null ? `${r.terps_total.toFixed(2)}%` : null;
    if (k === "mg_each") return r.mg_each != null ? `${r.mg_each}mg` : null;
    if (k === "pkg_mg") return r.pkg_mg != null ? `${r.pkg_mg}mg` : null;
    if (k === "added_date_visible")
      return r.added_date_visible ? formatAddedDate(r.added_date_visible) : null;
    return null;
  };

  const preferredLabels = useMemo(() => {
    return [...preferred]
      .map((k) => {
        const [id, platform] = k.split("|");
        const d = WD.dispensaries.find((x) => x.dispensary_id === id && x.platform === platform);
        return { key: k, label: d ? d.dispensary_name : k };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [preferred, WD]);

  return (
    <div className="query-pane">
      <div className="query-body">
        {/* Drawer overlay (mobile only, closes when tapped) */}
        {isMobile && filterDrawerOpen && (
          <div className="filter-overlay" onClick={() => setFilterDrawerOpen(false)} />
        )}

        {/* LEFT: filter rail — sidebar on desktop, slide-in drawer on mobile */}
        <aside
          className={
            "filter-rail" +
            (filterDrawerOpen ? " is-open" : "") +
            (!isMobile && railCollapsed ? " is-collapsed" : "")
          }
          style={
            isMobile ? undefined : { width: (railCollapsed ? 0 : railW) + "px", flex: "0 0 auto" }
          }
        >
          {/* Count + reset always visible at top so the keyboard can't hide it.
              Clicking the count closes the mobile drawer or collapses the
              desktop rail -- the topbar's Filter-N-products pill (app.tsx)
              is the way back in. Mobile also closes via tapping the overlay. */}
          <div className="rail-footer">
            <button
              className="rail-match-count"
              onClick={() => (isMobile ? setFilterDrawerOpen(false) : setRailCollapsed(true))}
            >
              <span className="rail-match-lbl">See </span>
              <span className="rail-match-num">{filtered.length.toLocaleString()}</span>
              <span className="rail-match-lbl"> product{filtered.length !== 1 ? "s" : ""}</span>
            </button>
            <button className="btn btn-ghost" onClick={resetFilters}>
              Reset filters
            </button>
          </div>

          {/* Scrollable filter content */}
          <div className="rail-body">
            <div className="rail-section">
              <div className="rail-section-title">Search</div>
              <div className="search-wrap">
                <svg
                  className="search-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                >
                  <circle cx="6" cy="6" r="4" />
                  <path d="M9 9l3 3" />
                </svg>
                <input
                  className="search-input"
                  type="text"
                  placeholder="brand · product · store"
                  value={filters.q}
                  onChange={(e) => update({ q: e.target.value })}
                />
                {filters.q && (
                  <button className="search-clear" onClick={() => update({ q: "" })}>
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Category chips are the active blob's own sub-categories -- only
              'other' (tincture + topical) has more than one, so the section
              is hidden everywhere else (DATA_CONTRACT.md / design-kickoff). */}
            {blob.categories.length > 1 && (
              <div className="rail-section">
                <div className="rail-section-title">Category</div>
                <ChipMulti
                  options={blob.categories}
                  value={filters.categories}
                  onChange={(v) => update({ categories: v })}
                />
              </div>
            )}

            {blob.strain_types.length > 0 && (
              <div className="rail-section">
                <div className="rail-section-title">Strain type</div>
                <ChipMulti
                  options={blob.strain_types}
                  value={filters.types}
                  onChange={(v) => update({ types: v })}
                />
              </div>
            )}

            {/* Form facets — extract/vape/edible only. The blob vocab is
              self-truncating (oils/formats/weights/edible_forms/doses only
              carry values its rows actually have), so these sections appear
              exactly where they apply: extract shows Form+Solventless+Size
              (oils is always empty for extract, so Solventless rides in the
              Form section instead of Type), vape shows Type+Format, edible
              shows Form+Dose. See backend/forms.py + DATA_CONTRACT.md. */}
            {(() => {
              const solventlessChip = blob.has_solventless && (
                <div className="chiprow">
                  <button
                    className={"chip " + (filters.solventless ? "is-on" : "")}
                    onClick={() => update({ solventless: !filters.solventless })}
                  >
                    solventless
                  </button>
                </div>
              );
              const isExtract = blob.blob === "extract";
              return (
                <>
                  {(blob.concentrate_forms?.length ?? 0) > 0 && (
                    <div className="rail-section">
                      <div className="rail-section-title">Form</div>
                      <ChipMulti
                        options={blob.concentrate_forms}
                        value={filters.concentrateForms}
                        onChange={(v) => update({ concentrateForms: v })}
                        label={(o) => facetLabel(CONCENTRATE_FORM_LABELS, o)}
                      />
                      {isExtract && solventlessChip}
                    </div>
                  )}

                  {!isExtract && ((blob.oils?.length ?? 0) > 0 || blob.has_solventless) && (
                    <div className="rail-section">
                      <div className="rail-section-title">Type</div>
                      {(blob.oils?.length ?? 0) > 0 && (
                        <ChipMulti
                          options={blob.oils}
                          value={filters.oils}
                          onChange={(v) => update({ oils: v })}
                          label={(o) => facetLabel(OIL_LABELS, o)}
                        />
                      )}
                      {solventlessChip}
                    </div>
                  )}
                </>
              );
            })()}

            {(blob.formats?.length ?? 0) > 0 && (
              <div className="rail-section">
                <div className="rail-section-title">Format</div>
                <ChipMulti
                  options={blob.formats}
                  value={filters.formats}
                  onChange={(v) => update({ formats: v })}
                  label={(o) => facetLabel(FORMAT_LABELS, o)}
                />
              </div>
            )}

            {(blob.weights?.length ?? 0) > 0 && (
              <div className="rail-section">
                <div className="rail-section-title">Size</div>
                <NumericChips
                  options={blob.weights}
                  value={filters.weights}
                  onChange={(v) => update({ weights: v })}
                  format={formatWeight}
                />
              </div>
            )}

            {(blob.edible_forms?.length ?? 0) > 0 && (
              <div className="rail-section">
                <div className="rail-section-title">Form</div>
                <ChipMulti
                  options={EDIBLE_FORM_ORDER.filter((f) => blob.edible_forms.includes(f))}
                  value={filters.edibleForms}
                  onChange={(v) => update({ edibleForms: v })}
                  label={(o) => facetLabel(EDIBLE_FORM_LABELS, o)}
                />
              </div>
            )}

            {(blob.doses?.length ?? 0) > 0 && (
              // No rail-section-title -- the control's own "Dose"
              // ctrl-label already names the axis (same reason the Ranges
              // section below carries no title: every control self-labels).
              // blob.doses is bracketed by an unbounded floor and ceiling so
              // both thumbs have an "off" position, matching every other
              // slider in the rail.
              <div className="rail-section">
                <SnappedRangeControl
                  label="Dose"
                  steps={[0, ...blob.doses, Infinity]}
                  value={filters.dose}
                  onChange={(v) => update({ dose: v })}
                  format={formatDose}
                />
              </div>
            )}

            {(blob.other_forms?.length ?? 0) > 0 && (
              <div className="rail-section">
                <div className="rail-section-title">Form</div>
                <ChipMulti
                  options={blob.other_forms}
                  value={filters.otherForms}
                  onChange={(v) => update({ otherForms: v })}
                  label={(o) => facetLabel(OTHER_FORM_LABELS, o)}
                />
              </div>
            )}

            {/* Flower Type: exclusion chips (frontend/DATA_CONTRACT.md). Every
              grade + infused starts hidden, so each chip is dark until clicked
              to bring that class in, and lit while its rows are in your
              results -- the same "lit = in results" metaphor as the other
              panels' include chips. Grade (small buds / pre-ground, from
              flower_forms) and infused are independent axes; a chip appears
              only when the blob actually carries that class. */}
            {(blob.has_infused || (blob.flower_forms?.length ?? 0) > 0) && (
              <div className="rail-section">
                <div className="rail-section-title">Type</div>
                <div className="chiprow">
                  {FLOWER_EXCLUDE_CHIPS.filter((c) =>
                    c.key === "infused" ? blob.has_infused : blob.flower_forms?.includes(c.key),
                  ).map((c) => {
                    const excluded = filters.flowerExclude.has(c.key);
                    return (
                      <button
                        key={c.key}
                        className={"chip " + (excluded ? "" : "is-on")}
                        title={(excluded ? "Show " : "Hide ") + c.hides}
                        onClick={() => {
                          const n = new Set(filters.flowerExclude);
                          if (n.has(c.key)) n.delete(c.key);
                          else n.add(c.key);
                          update({ flowerExclude: n });
                        }}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No section title -- every control here self-labels (Price,
              THC, terpene, Added), so a "Ranges" header just adds noise;
              the sliders make the range obvious. */}
            <div className="rail-section">
              {/* Price and Added are shared by every category; the potency
                controls are not. Unit-priced tabs hide the percent thc/cbd/
                terps columns because the percent is a concentration by mass,
                so offering percent sliders here filtered on a number the tab
                had decided not to show. THC becomes package milligrams and
                the terpene pair drops entirely.

                Hiding these was tried once and reverted, because the bounds
                stayed live with no control to reveal them. What makes it
                safe now is makeRowFilter skipping both percent axes for the
                same categories -- the control and the filter go together. */}
              <DualControl
                label={priceUnitLabel(blob.blob)}
                suffix="$"
                min={0}
                max={priceMode.max}
                step={PPG_STEP}
                value={filters.ppg}
                onChange={(v) => update({ ppg: v })}
              />
              {unitPriced ? (
                <DualControl
                  label="THC (mg)"
                  suffix="mg"
                  min={0}
                  max={PKG_MG_MAX}
                  step={5}
                  value={filters.pkgThc}
                  onChange={(v) => update({ pkgThc: v })}
                />
              ) : (
                <>
                  <DualControl
                    label="THC"
                    suffix="%"
                    min={0}
                    max={thcMode.max}
                    step={1}
                    value={filters.thc}
                    onChange={(v) => update({ thc: v })}
                  />
                  <select
                    className="select"
                    value={filters.terpName}
                    onChange={(e) => update({ terpName: e.target.value })}
                  >
                    <option value="any">Total terpenes</option>
                    {blob.terpene_names.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <DualControl
                    label={filters.terpName === "any" ? "Total %" : filters.terpName}
                    suffix="%"
                    min={0}
                    max={terpMode.max}
                    step={0.1}
                    value={filters.terp}
                    onChange={(v) => update({ terp: v })}
                  />
                </>
              )}
              <AgeControl
                value={filters.addedDaysAgo}
                onChange={(v) => update({ addedDaysAgo: v })}
              />
            </div>

            <div className="rail-section">
              <div className="rail-section-title">Brands</div>
              <div className="brand-scroll">
                <ChipMulti
                  options={blob.brands}
                  value={filters.brands}
                  onChange={(v) => update({ brands: v })}
                />
              </div>
            </div>
          </div>
          {/* end rail-body */}

          {/* Store filter summary — between scrollable content and sticky footer */}
          <div className={"pref-strip " + (preferredLabels.length === 0 ? "is-empty" : "")}>
            <div className="pref-strip-header">
              {preferredLabels.length === 0 ? (
                <span className="pref-strip-label">No store filter</span>
              ) : (
                <button
                  className={
                    "pref-strip-label pref-strip-toggle " + (prefExpanded ? "is-expanded" : "")
                  }
                  onClick={() => setPrefExpanded((x) => !x)}
                  aria-expanded={prefExpanded}
                >
                  Limiting to<span className="mono">{preferredLabels.length}</span>
                  {preferredLabels.length === 1 ? "store" : "stores"}
                  <span className="pref-strip-chevron">{prefExpanded ? "▴" : "▾"}</span>
                </button>
              )}
            </div>
            {prefExpanded && preferredLabels.length > 0 && (
              <ul className="pref-list">
                {preferredLabels.map((p) => (
                  <li key={p.key}>
                    <button
                      className="pref-list-row"
                      onClick={() => {
                        if (window.confirm(`Remove "${p.label}" from your store selection?`)) {
                          setPreferred((s) => {
                            const n = new Set(s);
                            n.delete(p.key);
                            return n;
                          });
                        }
                      }}
                    >
                      <span className="pref-list-name">{p.label}</span>
                      <span className="pref-list-remove-hint">click to remove</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <span className="col-resize-handle" onPointerDown={dragRail} />
        </aside>

        {/* RIGHT: results */}
        <section className="results-pane">
          {isMobile ? (
            <>
              <div className="m-sort-rail">
                {/* Overlaid on the rail's right edge (see .m-sort-rail
                    .collapse-all-btn), so it appears without shifting a
                    chip.  It covers the tail of the rail, which scrolls. */}
                {expandedDetail.size > 0 && (
                  <CollapseAllBtn onClick={() => setExpandedDetail(new Set())} label="close all" />
                )}
                <div className="m-sort-chips">
                  {(isPkgThc
                    ? MOBILE_SORT_CHIPS_PKG_THC
                    : isDose
                      ? MOBILE_SORT_CHIPS_DOSE
                      : MOBILE_SORT_CHIPS
                  ).map((chip) => {
                    const isActive = filters.sort.key === chip.sortKey;
                    const dir = isActive
                      ? filters.sort.dir
                      : (SORT_INITIAL_DIR[chip.sortKey] ?? "asc");
                    // The price chip spells out the per-category unit, matching
                    // the desktop column heading and the slider label.
                    const label =
                      chip.sortKey === "ppg" ? priceUnitLabel(blob.blob).toUpperCase() : chip.label;
                    return (
                      <button
                        key={chip.sortKey}
                        className={"m-sort-chip" + (isActive ? " is-active" : "")}
                        onClick={() => setSort(chip.sortKey)}
                      >
                        {dir === "desc" ? SORT_DESC : SORT_ASC} {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* One continuous list -- no paging. Only the rows around the
                viewport are in the DOM; the spacers stand in for the rest so
                the scrollbar still reflects the whole result set. */}
              {/* Positioning context for the scrubber, scoped to the list
                so the rail can't overlap the sort chips above it. */}
              <div className="m-list-wrap">
                <ul className="m-list" ref={mListRef}>
                  {vwin.padTop > 0 && (
                    <li className="m-spacer" style={{ height: vwin.padTop }} aria-hidden="true" />
                  )}
                  {sorted.slice(vwin.start, vwin.end).map((r) => (
                    <MobileRow
                      key={r.key}
                      r={r}
                      isOpen={expandedDetail.has(r.key)}
                      showStore={preferred.size !== 1}
                      sortVal={mobileSortVal(r)}
                      geoCircle={geoCircle}
                      storeSort={storeSort}
                      onStoreSort={onStoreSort}
                      onToggle={() => toggleDetail(r.key)}
                      onMeasure={(el) => vwin.measureRow(r.key, el, expandedDetail.has(r.key))}
                    />
                  ))}
                  {vwin.padBottom > 0 && (
                    <li
                      className="m-spacer"
                      style={{ height: vwin.padBottom }}
                      aria-hidden="true"
                    />
                  )}
                  {sorted.length === 0 && (
                    <li className="m-empty">
                      No products match. Loosen a filter or remove the store restriction.
                    </li>
                  )}
                </ul>
                {vwin.overflows && (
                  <ScrubBar
                    containerRef={mListRef}
                    fraction={vwin.scrollFraction}
                    value={mobileSortVal(sorted[vwin.firstVisible]) ?? null}
                    productName={sorted[vwin.firstVisible]?.product_name ?? null}
                  />
                )}
              </div>
            </>
          ) : (
            // The frame is the positioning box for the close-all pill, the
            // same arrangement as the mobile rail: the pill sits over the
            // header's visible right end, out of the columns' flow.  It
            // cannot live inside .table-wrap, whose scroll would carry it
            // off with the columns.
            <div className="table-frame">
              {expandedDetail.size > 0 && (
                <CollapseAllBtn onClick={() => setExpandedDetail(new Set())} label="close all" />
              )}
              <div className="table-wrap" ref={tableWrapRef}>
                <table className="results-table" style={{ minWidth: tableMinWidth }}>
                  <ResultsTableHead
                    columns={visibleCols}
                    sortKey={filters.sort.key}
                    sortDir={filters.sort.dir}
                    onSort={setSort}
                    sortInitialDir={SORT_INITIAL_DIR}
                    showInactiveSort
                  />
                  <tbody>
                    {dwin.padTop > 0 && (
                      <tr className="tr-spacer" aria-hidden="true">
                        <td colSpan={visibleCols.length} style={{ height: dwin.padTop }} />
                      </tr>
                    )}
                    {sorted.slice(dwin.start, dwin.end).map((r) => (
                      <DesktopRow
                        key={r.key}
                        r={r}
                        isOpen={expandedDetail.has(r.key)}
                        visibleCols={visibleCols}
                        geoCircle={geoCircle}
                        storeSort={storeSort}
                        onStoreSort={onStoreSort}
                        onToggle={() => toggleDetail(r.key)}
                        onMeasure={(el) => dwin.measureRow(r.key, el, expandedDetail.has(r.key))}
                      />
                    ))}
                    {dwin.padBottom > 0 && (
                      <tr className="tr-spacer" aria-hidden="true">
                        <td colSpan={visibleCols.length} style={{ height: dwin.padBottom }} />
                      </tr>
                    )}
                    {sorted.length === 0 && (
                      <tr>
                        <td colSpan={visibleCols.length} className="td-empty">
                          No products match. Loosen a filter or remove the store restriction.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No results footer: with both views on one continuous
            virtualized scroll there is no page to step through, and the
            match count it would carry is already on screen -- in the
            filter rail's own footer on desktop, in the topbar pill on
            mobile (and on desktop when the rail is collapsed). */}
        </section>
      </div>
      {/* end query-body */}
    </div>
  );
}

// ──────────────────────────── helpers ──────────────────────────────────────

export function hasActiveFilters(f: Filters, category: string): boolean {
  // A range slider is active when either thumb has left its extreme.
  // Bounds are stored in the active category's scale, so "below max"
  // is judged against that category's mode max.
  const percent = !isUnitPriced(category);
  return (
    f.q !== "" ||
    f.categories.size > 0 ||
    f.types.size > 0 ||
    f.brands.size > 0 ||
    f.oils.size > 0 ||
    f.formats.size > 0 ||
    f.weights.size > 0 ||
    f.solventless ||
    f.concentrateForms.size > 0 ||
    f.edibleForms.size > 0 ||
    f.dose[0] > 0 ||
    isFinite(f.dose[1]) ||
    // The resting state hides every class; any chip brought in is a filter.
    DEFAULT_FLOWER_EXCLUDE.some((k) => !f.flowerExclude.has(k)) ||
    f.otherForms.size > 0 ||
    // Only the axes this category actually filters on -- the percent
    // potency bounds are inert on the unit-priced tabs (makeRowFilter),
    // so counting them would light up "clear" over nothing.
    (percent && f.thc[0] > 0) ||
    (percent && f.thc[1] < thcModeFor(category).max) ||
    (!percent && f.pkgThc[0] > 0) ||
    (!percent && f.pkgThc[1] < PKG_MG_MAX) ||
    f.ppg[0] > 0 ||
    f.ppg[1] < priceModeFor(category).max ||
    (percent && f.terpName !== "any") ||
    (percent && f.terp[0] > 0) ||
    (percent && f.terp[1] < terpModeFor(category).max) ||
    f.addedDaysAgo[0] > 0 ||
    isFinite(f.addedDaysAgo[1])
  );
}

export function defaultFilters(): Filters {
  return {
    q: "",
    categories: new Set<string>(),
    types: new Set<string>(),
    brands: new Set<string>(),
    oils: new Set<string>(),
    formats: new Set<string>(),
    weights: new Set<number>(),
    solventless: false,
    concentrateForms: new Set<string>(),
    edibleForms: new Set<string>(),
    dose: [0, Infinity],
    flowerExclude: new Set<string>(DEFAULT_FLOWER_EXCLUDE),
    otherForms: new Set<string>(),
    // Upper bounds sit at the widest category's cap: any bound at or
    // beyond the active category's mode max reads as unbounded in the
    // pipeline, so these defaults filter nothing in every category.
    thc: [0, THC_MAX_POTENT],
    pkgThc: [0, PKG_MG_MAX],
    ppg: [0, PPG_MAX_GRAM],
    terpName: "any",
    terp: [0, TERP_MAX_POTENT],
    addedDaysAgo: [0, Infinity],
    sort: { key: "ppg", dir: "asc" },
  };
}

function formatAddedDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const [, m, d] = dateStr.split("-");
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}

// `form` is raw source data -- inconsistent casing ("LIVE Rosin" vs "kief")
// and, on some platforms, an immediately-repeated word ("troches troches",
// "cartridge cartridge") from how the scraped subcategory gets concatenated.
// Title-case it and drop adjacent duplicates for display; returns null for
// blank/missing forms so callers can omit the tag entirely.
function formatForm(form: string | null): string | null {
  if (!form) return null;
  const out: string[] = [];
  for (const w of form.trim().split(/\s+/)) {
    if (out.length && out[out.length - 1].toLowerCase() === w.toLowerCase()) continue;
    out.push(w[0].toUpperCase() + w.slice(1).toLowerCase());
  }
  return out.length ? out.join(" ") : null;
}
